import React, { useState } from "react";
import EpisodeList from "../../components/movie/EpisodeList";
import { PostTypeKey } from "../../types/common.type";
import { t } from "i18next";
import { useDispatch } from 'react-redux';
import { setNovelStyle } from '../../store/slices/settingSlice';

interface ChapterControllerProps {
  currentIdx: number;
  chaptersArr: any[];
  goToChapter: (idx: number) => void;
  slug: string;
  currentChapter: any;
  type?: PostTypeKey; // Để tái sử dụng cho nhiều loại (comic, movie, ...)
  setHideUI?: any; // Callback để điều khiển UI
  hideUI?: boolean; // Trạng thái ẩn/hiện UI
  // Novel filter props
  onApplyNovelStyle?: (style: any) => void;
  novelStyle?: any;
}

const ChapterController: React.FC<ChapterControllerProps> = ({
  currentIdx,
  chaptersArr,
  goToChapter,
  slug,
  currentChapter,
  type = "comic",
  setHideUI,
  hideUI = false, // Trạng thái ẩn/hiện UI
  onApplyNovelStyle,
  novelStyle,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [tempStyle, setTempStyle] = useState({
    fontFamily: novelStyle?.fontFamily || "serif",
    fontSize: novelStyle?.fontSize || 18,
    color: novelStyle?.color || "#fff",
    background: novelStyle?.background || "#1a1a1a",
  });

  const dispatch = useDispatch();

  const handleOpenList = () => setDrawerOpen(true);
  const handleCloseList = () => setDrawerOpen(false);

  return (
    <>
      {hideUI && (
        <button
          className="fixed top-4 right-4 z-50 bg-gray-800 bg-opacity-80 rounded-full p-2 hover:bg-gray-700 transition"
          style={{ border: "none" }}
          onClick={() => setHideUI(false)}
          aria-label="Hiện giao diện"
          title="Hiện giao diện"
        >
          <img
            src={"/assets/icons/eye-off.svg"}
            alt="Hiện giao diện"
            width={28}
            height={28}
          />
        </button>
      )}
      {!hideUI && (
        <div className="fixed bottom-0 left-0 w-full z-50 flex flex-col items-center pointer-events-none">
          <div className="flex flex-row gap-6 items-center w-full  bg-black shadow-[0_-4px_24px_0_rgba(0,0,0,0.7)] px-2 sm:px-6 py-3 pointer-events-auto rounded-t-xl mx-auto">
            <div className="flex-1 flex flex-col items-center gap-2">
              <div className="flex justify-center gap-4 sm:gap-6 w-full">
                <div className="flex flex-col items-center">
                  <button
                    className={`w-14 h-14 flex items-center justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed ${
                      currentIdx <= 1
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "hover:bg-primary text-white"
                    } bg-transparent`}
                    style={{ border: "none", background: "none" }}
                    onClick={() => goToChapter(currentIdx - 1)}
                    disabled={currentIdx <= 1}
                    aria-label="Chương trước"
                    title="Chương trước"
                  >
                    <img
                      src={"/assets/icons/prev.svg"}
                      alt="prev"
                      width={28}
                      height={28}
                    />
                  </button>
                  <span
                    className={`hidden md:block text-[11px] font-bold mt-0.5 leading-tight ${
                      currentIdx <= 1 ? "text-gray-500" : "text-white"
                    }`}
                  >
                    Tập trước
                  </span>
                </div>
                {type === "novel" && (
                  <div className="flex flex-col items-center">
                    <button
                      className="w-14 h-14 flex items-center justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-primary bg-transparent hover:bg-primary text-white"
                      style={{ border: "none", background: "none" }}
                      onClick={() => setShowFilter(true)}
                      aria-label="Danh sách chương"
                      title="Danh sách chương"
                      disabled={false}
                    >
                      <img
                        src={"/assets/icons/filter-chapter.svg"}
                        alt="list"
                        width={28}
                        height={28}
                      />
                    </button>
                    <span className="text-[11px] font-bold mt-0.5 leading-tight text-white hidden md:block">
                      Bộ lọc
                    </span>
                  </div>
                )}
                <div className="flex flex-col items-center">
                  <button
                    className="w-14 h-14 flex items-center justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-primary bg-transparent hover:bg-primary text-white"
                    style={{ border: "none", background: "none" }}
                    onClick={handleOpenList}
                    aria-label="Danh sách chương"
                    title="Danh sách chương"
                    disabled={false}
                  >
                    <img
                      src={"/assets/icons/list-chapter.svg"}
                      alt="list"
                      width={28}
                      height={28}
                    />
                  </button>
                  <span className="text-[11px] font-bold mt-0.5 leading-tight text-white hidden md:block">
                    Danh sách tập
                  </span>
                </div>

                <div className="flex flex-col items-center">
                  <button
                    className={`w-14 h-14 flex items-center justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed ${
                      currentIdx >= chaptersArr.length
                        ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                        : "hover:bg-primary text-white"
                    } bg-transparent`}
                    style={{ border: "none", background: "none" }}
                    onClick={() => {
                      setLoading(true);
                      goToChapter(currentIdx + 1);
                      setLoading(false);
                    }}
                    disabled={currentIdx >= chaptersArr.length || loading}
                    aria-label="Chương sau"
                    title="Chương sau"
                  >
                    {loading ? (
                      <svg
                        className="animate-spin"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                    ) : (
                      <img
                        src={"/assets/icons/next.svg"}
                        alt="next"
                        width={28}
                        height={28}
                      />
                    )}
                  </button>
                  <span
                    className={`text-[11px] font-bold mt-0.5 leading-tight hidden md:block ${
                      currentIdx >= chaptersArr.length || loading
                        ? "text-gray-500"
                        : "text-white"
                    }`}
                  >
                    Tập tiếp theo
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <button
                    className={`w-14 h-14 flex items-center  justify-center shadow-lg transition focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed bg-transparent`}
                    style={{ border: "none", background: "none" }}
                    onClick={() => setHideUI(true)}
                    aria-label="Ẩn giao diện"
                    title="Ẩn giao diện"
                  >
                    <img
                      src={"/assets/icons/eye.svg"}
                      alt="Ẩn giao diện"
                      width={24}
                      height={24}
                    />
                  </button>
                  <span
                    className={`text-[11px] font-bold mt-0.5 leading-tight text-white hidden md:block`}
                  >
                    Ẩn giao diện
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFilter && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black bg-opacity-40"
          onClick={() => setShowFilter(false)}
        >
          <div
            className="w-full max-w-md bg-black rounded-t-2xl p-6 pb-8 shadow-lg animate-slide-in-up"
            style={{ marginBottom: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4 text-center">
              Bộ lọc hiển thị
            </h3>
            <div className="mb-4">
              <label className="block mb-1 font-semibold">Kiểu chữ</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  className={`px-3 py-2 rounded border transition font-serif ${tempStyle.fontFamily === 'serif' ? 'bg-primary text-white border-primary' : 'bg-gray-800 text-white border-gray-700'}`}
                  style={{fontFamily: 'serif'}}
                  onClick={() => setTempStyle({...tempStyle, fontFamily: 'serif'})}
                >
                  Serif
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 rounded border transition font-sans ${tempStyle.fontFamily === 'sans-serif' ? 'bg-primary text-white border-primary' : 'bg-gray-800 text-white border-gray-700'}`}
                  style={{fontFamily: 'sans-serif'}}
                  onClick={() => setTempStyle({...tempStyle, fontFamily: 'sans-serif'})}
                >
                  Sans-serif
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 rounded border transition font-mono ${tempStyle.fontFamily === 'monospace' ? 'bg-primary text-white border-primary' : 'bg-gray-800 text-white border-gray-700'}`}
                  style={{fontFamily: 'monospace'}}
                  onClick={() => setTempStyle({...tempStyle, fontFamily: 'monospace'})}
                >
                  Monospace
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-semibold">
                Kích thước chữ: {tempStyle.fontSize}px
              </label>
              <input
                type="range"
                min={12}
                max={100}
                value={tempStyle.fontSize}
                onChange={(e) =>
                  setTempStyle({
                    ...tempStyle,
                    fontSize: Number(e.target.value),
                  })
                }
                className="w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-semibold">Màu chữ</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={tempStyle.color}
                  onChange={(e) =>
                    setTempStyle({ ...tempStyle, color: e.target.value })
                  }
                  className="w-10 h-10 p-0 border-none bg-transparent"
                  style={{ cursor: "pointer" }}
                />
                <span className="text-white text-sm">{getColorName(tempStyle.color)}</span>
              </div>
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-semibold">Màu nền</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={tempStyle.background}
                  onChange={(e) =>
                    setTempStyle({ ...tempStyle, background: e.target.value })
                  }
                  className="w-10 h-10 p-0 border-none bg-transparent"
                  style={{ cursor: "pointer" }}
                />
                <span className="text-white text-sm">{getColorName(tempStyle.background)}</span>
              </div>
            </div>
            <button
              className="w-full bg-primary text-white py-2 rounded font-bold mt-2 hover:bg-opacity-90 transition"
              onClick={() => {
                onApplyNovelStyle && onApplyNovelStyle(tempStyle);
                dispatch(setNovelStyle(tempStyle));
                setShowFilter(false);
              }}
            >
              Áp dụng
            </button>
          </div>
          <style>{`
            @keyframes slide-in-up {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
            .animate-slide-in-up {
              animation: slide-in-up 0.3s cubic-bezier(0.4,0,0.2,1);
            }
          `}</style>
        </div>
      )}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black bg-opacity-40"
            onClick={handleCloseList}
          ></div>
          <div className="w-[350px] max-w-full h-full bg-gray-900 shadow-lg p-4 overflow-y-auto animate-slide-in-right">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-bold">Danh sách chương</span>
              <button onClick={handleCloseList} className="text-white text-2xl">
                ×
              </button>
            </div>
            <EpisodeList
              episodes={chaptersArr}
              movieSlug={slug || ""}
              viewMode="list"
              currentVideoUrl={String(currentChapter.id)}
              onPlayEpisode={(id) => {
                goToChapter(
                  Number(
                    chaptersArr.find((c: any) => String(c.id) === id)?.index
                  )
                );
                handleCloseList();
              }}
              type={type}
            />
          </div>
        </div>
      )}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s cubic-bezier(0.4,0,0.2,1);
        }
      `}</style>
    </>
  );
};

// Helper function for color name
function getColorName(hex: string) {
  const map: Record<string, string> = {
    '#ffffff': 'Trắng',
    '#fff': 'Trắng',
    '#000000': 'Đen',
    '#000': 'Đen',
    '#ff0000': 'Đỏ',
    '#00ff00': 'Xanh lá',
    '#0000ff': 'Xanh dương',
    '#ffff00': 'Vàng',
    '#00ffff': 'Cyan',
    '#ff00ff': 'Hồng',
    '#808080': 'Xám',
    '#c0c0c0': 'Bạc',
    '#800000': 'Nâu',
    '#008000': 'Xanh lá đậm',
    '#808000': 'Olive',
    '#800080': 'Tím',
    '#008080': 'Xanh ngọc',
    '#ffa500': 'Cam',
    '#1a1a1a': 'Đen nhạt',
    '#f5f5f5': 'Xám nhạt',
  };
  const hexLower = hex.toLowerCase();
  return map[hexLower] || hex;
}

export default ChapterController;
